import {
  Directive,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';

/**
 * Pins the host element with `position: sticky` at a `top` offset equal to
 * the combined rendered height of the elements matched by the given CSS
 * selector(s) (comma-separated, same syntax as `querySelectorAll`) — rather
 * than a hardcoded pixel sum, which silently drifts whenever a stacked bar's
 * own height changes (including going from 0 to its full height when a
 * conditionally-rendered bar like the account/caller bar toggles on).
 *
 * Usage: `<app-module-header jfwStickyStackTop=".account-topper">`
 *
 * With no value bound (or an empty string) the directive is a no-op — it
 * never touches the host's positioning at all. This matters for a shared
 * component used in more than one context (e.g. `order-list-view`, also
 * consumed by Data Pods/dealer-portal): a consumer can leave the input
 * unbound to keep the host's own default (non-sticky) styling untouched,
 * and only the one context that actually wants pinned-stack behavior binds
 * a real selector.
 */
@Directive({
  selector: '[jfwStickyStackTop]',
  standalone: true,
})
export class StickyStackTopDirective implements OnInit, OnDestroy {
  public readonly stackAbove = input<string>('', {
    alias: 'jfwStickyStackTop',
  });

  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private aboveElements: HTMLElement[] = [];

  ngOnInit(): void {
    if (!this.stackAbove()) return;

    this.refreshObservedElements();

    // A stacked bar's own visibility is often toggled via a class binding on
    // an element that already exists (e.g. account-setter's host only gains
    // `.account-topper` once its suite-check signal resolves, asynchronously,
    // after this directive's ngOnInit already ran) -- a plain one-time
    // querySelectorAll here would permanently miss it, since that's an
    // attribute mutation on an existing node, not a childList insertion a
    // ResizeObserver on the (not-yet-matched) target could ever catch.
    // Watching the whole document for class/childList changes re-resolves
    // the selector whenever anything in the stack could plausibly change.
    if (typeof MutationObserver !== 'undefined') {
      this.mutationObserver = new MutationObserver(() =>
        this.refreshObservedElements(),
      );
      this.mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  private refreshObservedElements(): void {
    const elements = Array.from(
      document.querySelectorAll<HTMLElement>(this.stackAbove()),
    );
    const unchanged =
      elements.length === this.aboveElements.length &&
      elements.every((el, i) => el === this.aboveElements[i]);
    if (unchanged) {
      this.measure();
      return;
    }

    this.aboveElements = elements;
    // ResizeObserver isn't defined in the Jest/jsdom test environment.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver?.disconnect();
      this.resizeObserver = new ResizeObserver(() => this.measure());
      elements.forEach((el) => this.resizeObserver!.observe(el));
    }
    this.measure();
  }

  private measure(): void {
    // Not rounded: at fractional display scaling (e.g. Windows 125%) a bar's
    // height is fractional, and rounding up leaves a one-device-pixel seam
    // that content scrolls through visibly (EXT-6406).
    const top = this.aboveElements.reduce(
      (sum, el) => sum + el.getBoundingClientRect().height,
      0,
    );
    this.renderer.setStyle(this.elementRef.nativeElement, 'position', 'sticky');
    this.renderer.setStyle(this.elementRef.nativeElement, 'top', `${top}px`);
  }
}
